<template>
  <div class="flex-1 overflow-auto p-4">
    <!-- List Canned Response -->
    <div class="flex flex-row gap-4">
      <div class="w-[60%]">
        <p
          v-if="!inboxesList.length"
          class="flex h-full items-center flex-col justify-center"
        >
          {{ $t('INBOX_MGMT.LIST.404') }}
          <router-link
            v-if="isAdmin"
            :to="addAccountScoping('settings/inboxes/new')"
          >
            {{ $t('SETTINGS.INBOXES.NEW_INBOX') }}
          </router-link>
        </p>

        <table v-if="inboxesList.length" class="woot-table">
          <tbody>
            <tr v-for="item in inboxesList" :key="item.id">
              <td>
                <img
                  v-if="item.avatar_url"
                  class="woot-thumbnail"
                  :src="item.avatar_url"
                  alt="No Page Image"
                />
                <img
                  v-else
                  class="woot-thumbnail"
                  src="~dashboard/assets/images/flag.svg"
                  alt="No Page Image"
                />
              </td>
              <!-- Short Code  -->
              <td>
                <span class="agent-name">{{ item.name }}</span>
                <span v-if="item.channel_type === 'Channel::FacebookPage'">
                  Facebook
                </span>
                <span v-if="item.channel_type === 'Channel::WebWidget'">
                  Website
                </span>
                <span v-if="item.channel_type === 'Channel::TwitterProfile'">
                  Twitter
                </span>
                <span v-if="item.channel_type === 'Channel::TwilioSms'">
                  {{ twilioChannelName(item) }}
                </span>
                <span v-if="item.channel_type === 'Channel::Whatsapp'">
                  Whatsapp
                </span>
                <span v-if="item.channel_type === 'Channel::Sms'">
                  Sms
                </span>
                <span v-if="item.channel_type === 'Channel::Email'">
                  Email
                </span>
                <span v-if="item.channel_type === 'Channel::Telegram'">
                  Telegram
                </span>
                <span v-if="item.channel_type === 'Channel::Line'">Line</span>
                <span v-if="item.channel_type === 'Channel::Api'">
                  {{ globalConfig.apiChannelName || 'API' }}
                </span>
                <span
                  v-if="item.disabled_at"
                  v-tooltip.top="item.disable_reason"
                  class="inbox-badge inbox-badge--disabled"
                >
                  {{ $t('INBOX_MGMT.DISABLE.BADGE') }}
                </span>
                <span
                  v-if="item.channel_missing"
                  v-tooltip.top="$t('INBOX_MGMT.CHANNEL_MISSING.TOOLTIP')"
                  class="inbox-badge inbox-badge--broken"
                >
                  {{ $t('INBOX_MGMT.CHANNEL_MISSING.BADGE') }}
                </span>
              </td>

              <!-- Action Buttons -->
              <td>
                <div class="button-wrapper">
                  <router-link
                    :to="addAccountScoping(`settings/inboxes/${item.id}`)"
                  >
                    <woot-button
                      v-if="isAdmin"
                      v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                      variant="smooth"
                      size="tiny"
                      icon="settings"
                      color-scheme="secondary"
                      class-names="grey-btn"
                    />
                  </router-link>

                  <!-- shown for every disabled inbox, orphan included: enable is
                       the only way back out, so hiding it would strand the row -->
                  <woot-button
                    v-if="isAdmin && item.disabled_at"
                    v-tooltip.top="$t('INBOX_MGMT.DISABLE.ENABLE_BUTTON_TEXT')"
                    variant="smooth"
                    color-scheme="success"
                    size="tiny"
                    class-names="grey-btn"
                    :is-loading="loading[item.id]"
                    icon="play-circle"
                    @click="enableInbox(item)"
                  />

                  <!-- single entry point: deletes the inbox when it is empty, and
                       offers to disable it when it carries history. On an already
                       disabled inbox only the delete case remains -- disabling it
                       again is meaningless, and an inbox with history is exactly
                       what we are protecting. -->
                  <woot-button
                    v-if="isAdmin && (!item.disabled_at || !item.has_activity)"
                    v-tooltip.top="removeTooltip(item)"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    class-names="grey-btn"
                    :is-loading="loading[item.id]"
                    icon="dismiss-circle"
                    @click="openRemove(item)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="w-[34%]">
        <span
          v-dompurify-html="
            useInstallationName(
              $t('INBOX_MGMT.SIDEBAR_TXT'),
              globalConfig.installationName
            )
          "
        />
      </div>
    </div>
    <settings
      v-if="showSettings"
      :show.sync="showSettings"
      :on-close="closeSettings"
      :inbox="selectedInbox"
    />

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      :show.sync="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <disable-inbox-modal
      v-if="showDisablePopup"
      :show.sync="showDisablePopup"
      :inbox="selectedInbox"
      @disabled="closeDisable"
      @cancel="closeDisable"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import Settings from './Settings';
import DisableInboxModal from './DisableInboxModal';
import adminMixin from '../../../../mixins/isAdmin';
import accountMixin from '../../../../mixins/account';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';

export default {
  components: {
    Settings,
    DisableInboxModal,
  },
  mixins: [adminMixin, accountMixin, globalConfigMixin],
  data() {
    return {
      loading: {},
      showSettings: false,
      showDeletePopup: false,
      showDisablePopup: false,
      selectedInbox: {},
    };
  },
  computed: {
    ...mapGetters({
      inboxesList: 'inboxes/getInboxes',
      globalConfig: 'globalConfig/get',
    }),
    // Delete Modal
    deleteConfirmText() {
      return `${this.$t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${
        this.selectedInbox.name
      }`;
    },
    deleteRejectText() {
      return `${this.$t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${
        this.selectedInbox.name
      }`;
    },
    confirmDeleteMessage() {
      return `${this.$t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${
        this.selectedInbox.name
      }?`;
    },
    confirmPlaceHolderText() {
      return `${this.$t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
        inboxName: this.selectedInbox.name,
      })}`;
    },
  },
  methods: {
    twilioChannelName(item) {
      const { medium = '' } = item;
      if (medium === 'whatsapp') return 'WhatsApp';
      return 'Twilio SMS';
    },
    openSettings(inbox) {
      this.showSettings = true;
      this.selectedInbox = inbox;
    },
    closeSettings() {
      this.showSettings = false;
      this.selectedInbox = {};
    },
    async deleteInbox({ id }) {
      try {
        await this.$store.dispatch('inboxes/delete', id);
        bus.$emit(
          'newToastMessage',
          this.$t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        bus.$emit(
          'newToastMessage',
          error?.response?.data?.message ||
            this.$t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
        );
      }
    },

    confirmDeletion() {
      this.deleteInbox(this.selectedInbox);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeletePopup = false;
      this.selectedInbox = {};
    },

    // The remove button is the single entry point. An empty inbox -- orphan or
    // not, disabled or not -- is deleted outright; one that carries history is
    // offered as a disable instead, because deleting it would purge media and
    // reports. An orphan with history is the one case left with nothing to do
    // from here: there is no channel to take out of operation.
    removeAction(inbox) {
      if (!inbox.has_activity) return 'delete';
      if (inbox.channel_missing) return 'blocked-orphan';
      return 'disable';
    },
    removeTooltip(inbox) {
      switch (this.removeAction(inbox)) {
        case 'delete':
          return this.$t('INBOX_MGMT.DELETE.BUTTON_TEXT');
        case 'disable':
          return this.$t('INBOX_MGMT.DISABLE.BUTTON_TEXT');
        default:
          // clicking only explains why nothing can be done from here
          return this.$t('INBOX_MGMT.DELETE.CANNOT_REMOVE');
      }
    },
    openRemove(inbox) {
      this.selectedInbox = inbox;
      const action = this.removeAction(inbox);
      if (action === 'delete') {
        this.showDeletePopup = true;
        return;
      }
      if (action === 'disable') {
        this.showDisablePopup = true;
        return;
      }
      this.selectedInbox = {};
      bus.$emit(
        'newToastMessage',
        this.$t('INBOX_MGMT.CHANNEL_MISSING.WITH_HISTORY')
      );
    },
    closeDisable() {
      this.showDisablePopup = false;
      this.selectedInbox = {};
    },
    async enableInbox(inbox) {
      this.$set(this.loading, inbox.id, true);
      try {
        await this.$store.dispatch('inboxes/enable', inbox.id);
        bus.$emit(
          'newToastMessage',
          this.$t('INBOX_MGMT.DISABLE.API.ENABLE_SUCCESS_MESSAGE')
        );
      } catch (error) {
        bus.$emit(
          'newToastMessage',
          error?.response?.data?.message ||
            this.$t('INBOX_MGMT.DISABLE.API.ENABLE_ERROR_MESSAGE')
        );
      } finally {
        this.$set(this.loading, inbox.id, false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.inbox-badge {
  border-radius: var(--border-radius-normal);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  margin-left: var(--space-smaller);
  padding: var(--space-micro) var(--space-smaller);
  white-space: nowrap;
}

.inbox-badge--disabled {
  background: var(--y-50);
  color: var(--y-800);
}

.inbox-badge--broken {
  background: var(--r-50);
  color: var(--r-800);
}
</style>
